import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';

function Terms() {
  const [content, setContent] = useState<string | null>(null);
  const [title, setTitle] = useState('Terms of Service');
  const [effectiveDate, setEffectiveDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoc = async () => {
      const { data } = await supabase
        .from('legal_documents')
        .select('title, content, effective_date')
        .eq('type', 'terms_of_service')
        .eq('is_active', true)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setTitle(data.title);
        setContent(data.content);
        setEffectiveDate(data.effective_date);
      }
      setLoading(false);
    };

    fetchDoc();
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (content) {
      const html = DOMPurify.sanitize(marked(content) as string);
      return (
        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    return (
      <div className="prose prose-lg">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing and participating in the Ibom Open Golf Tournament ("Tournament"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not participate in the Tournament.
        </p>

        <h2>2. Tournament Registration</h2>
        <p>2.1. Registration is subject to availability and is on a first-come, first-served basis.</p>
        <p>2.2. All participants must meet the eligibility criteria as specified in the Tournament rules.</p>
        <p>2.3. Registration fees are non-refundable unless the Tournament is cancelled by the organizers.</p>

        <h2>3. Player Conduct</h2>
        <p>3.1. All players must adhere to the Rules of Golf as approved by R&amp;A and USGA.</p>
        <p>3.2. Players must maintain professional conduct throughout the Tournament.</p>
        <p>3.3. The Tournament committee reserves the right to disqualify any player for misconduct.</p>

        <h2>4. Tournament Format</h2>
        <p>4.1. The Tournament format and schedule are subject to change at the discretion of the organizers.</p>
        <p>4.2. Weather-related adjustments may be made to ensure player safety and fair competition.</p>

        <h2>5. Prize Money and Awards</h2>
        <p>5.1. Prize money will be distributed according to the official prize breakdown structure.</p>
        <p>5.2. All prizes are subject to applicable taxes and regulations.</p>

        <h2>6. Media Rights</h2>
        <p>6.1. The Tournament reserves all media rights including photographs, video, and digital content.</p>
        <p>6.2. Players grant permission for use of their image and likeness for promotional purposes.</p>

        <h2>7. Liability and Insurance</h2>
        <p>7.1. Players participate at their own risk and are responsible for their own insurance coverage.</p>
        <p>7.2. The Tournament organizers are not liable for any injury, loss, or damage to players or their equipment.</p>

        <h2>8. Cancellation Policy</h2>
        <p>8.1. The Tournament may be cancelled or postponed due to circumstances beyond our control.</p>
        <p>8.2. In case of cancellation, the refund policy will be communicated to all registered players.</p>

        <h2>9. Amendments</h2>
        <p>
          The Tournament organizers reserve the right to amend these Terms at any time. Changes will be communicated to registered players and posted on the official website.
        </p>

        <h2>10. Contact Information</h2>
        <p>
          For questions about these Terms, please contact:
          <br />
          Tournament Director
          <br />
          Ibom Golf &amp; Country Club
          <br />
          Uyo, Akwa Ibom State, Nigeria
          <br />
          Email: legal@ibomopen.com
        </p>
      </div>
    );
  };

  return (
    <div className="pt-20">
      <section className="py-16 bg-golf-sand">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-golf-gray mb-8">
              {title}
            </h1>
            {effectiveDate && (
              <p className="text-gray-600 mb-4">
                Last updated: {new Date(effectiveDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            {!effectiveDate && !loading && (
              <p className="text-gray-600 mb-4">Last updated: March 15, 2025</p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Terms;
